import { useState, useMemo } from "react";
import { GripVertical, Trash2, Video, ExternalLink, AlertCircle, Youtube, ChevronUp, ChevronDown } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { getVideoEmbedUrl, sanitizeVideoEmbedCode, isEmbedCode } from "@/lib/videoEmbed";
import type { VideoBlock } from "@/types/helpArticle";
import type { DraggableAttributes } from "@dnd-kit/core";
import type { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";

interface DragHandleProps {
  attributes: DraggableAttributes;
  listeners: SyntheticListenerMap | undefined;
}

interface MobileReorderProps {
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

interface VideoBlockEditorProps {
  block: VideoBlock;
  onChange: (block: VideoBlock) => void;
  onDelete: () => void;
  dragHandleProps?: DragHandleProps;
  mobileReorderProps?: MobileReorderProps;
}

interface VideoValidationResult {
  isValid: boolean;
  embedUrl: string | null;
  platform: "youtube" | "vimeo" | null;
  originalUrl: string | null;
  error: string | null;
}

/**
 * Validates and extracts video information from a URL or embed code
 */
function validateVideoInput(input: string): VideoValidationResult {
  if (!input || !input.trim()) {
    return { isValid: false, embedUrl: null, platform: null, originalUrl: null, error: null };
  }

  const trimmed = input.trim();

  // Check if it's an embed code (iframe)
  if (isEmbedCode(trimmed)) {
    const result = sanitizeVideoEmbedCode(trimmed);
    if (!result.isValid) {
      return { 
        isValid: false, 
        embedUrl: null, 
        platform: null, 
        originalUrl: null, 
        error: result.error 
      };
    }
    
    const platform = result.embedUrl?.includes("vimeo") ? "vimeo" : "youtube";
    return {
      isValid: true,
      embedUrl: result.embedUrl,
      platform,
      originalUrl: result.embedUrl,
      error: null,
    };
  }

  // It's a URL - validate and convert
  const url = trimmed.toLowerCase();
  
  // Check for YouTube
  const isYouTube = url.includes("youtube.com") || url.includes("youtu.be");
  
  // Check for Vimeo
  const isVimeo = url.includes("vimeo.com") || url.includes("player.vimeo.com");

  if (!isYouTube && !isVimeo) {
    return {
      isValid: false,
      embedUrl: null,
      platform: null,
      originalUrl: null,
      error: "Please enter a valid YouTube or Vimeo URL",
    };
  }

  const embedUrl = getVideoEmbedUrl(trimmed);
  
  // Verify the embed URL was successfully generated
  if (!embedUrl.includes("/embed/") && !embedUrl.includes("/video/")) {
    return {
      isValid: false,
      embedUrl: null,
      platform: null,
      originalUrl: null,
      error: "Could not extract video ID. Please check the URL format.",
    };
  }

  return {
    isValid: true,
    embedUrl,
    platform: isYouTube ? "youtube" : "vimeo",
    originalUrl: trimmed,
    error: null,
  };
}

/**
 * Gets the watch URL from an embed URL
 */
function getWatchUrl(embedUrl: string, platform: "youtube" | "vimeo" | null): string | null {
  if (!embedUrl || !platform) return null;

  if (platform === "youtube") {
    const match = embedUrl.match(/embed\/([^?&]+)/);
    if (match) {
      return `https://www.youtube.com/watch?v=${match[1]}`;
    }
  }

  if (platform === "vimeo") {
    const match = embedUrl.match(/video\/(\d+)/);
    if (match) {
      return `https://vimeo.com/${match[1]}`;
    }
  }

  return null;
}

export function VideoBlockEditor({
  block,
  onChange,
  onDelete,
  dragHandleProps,
  mobileReorderProps,
}: VideoBlockEditorProps) {
  const [isTouched, setIsTouched] = useState(false);

  // Validate and get embed information
  const validation = useMemo(() => {
    return validateVideoInput(block.url);
  }, [block.url]);

  const handleUrlChange = (url: string) => {
    onChange({ ...block, url });
  };

  const handleTitleChange = (title: string) => {
    onChange({ ...block, title });
  };

  const handleBlur = () => {
    setIsTouched(true);
  };

  const watchUrl = useMemo(() => {
    if (validation.isValid && validation.embedUrl && validation.platform) {
      return getWatchUrl(validation.embedUrl, validation.platform);
    }
    return null;
  }, [validation]);

  const platformLabel = validation.platform === "youtube" ? "YouTube" : validation.platform === "vimeo" ? "Vimeo" : null;

  return (
    <div className="group flex gap-2 sm:gap-3 p-3 sm:p-4 border rounded-lg bg-card hover:border-primary/50 transition-colors">
      {/* Drag Handle - hidden on mobile */}
      <div
        className="hidden sm:flex items-center cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground touch-none"
        {...dragHandleProps?.attributes}
        {...dragHandleProps?.listeners}
      >
        <GripVertical className="h-5 w-5" />
      </div>

      {/* Content */}
      <div className="flex-1 space-y-3 sm:space-y-4 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <Label className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Video className="h-4 w-4 shrink-0" />
            <span className="hidden sm:inline">Embedded Video</span>
            <span className="sm:hidden">Video</span>
          </Label>
          <div className="flex items-center gap-1">
            {/* Mobile reorder buttons */}
            <div className="flex sm:hidden">
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveUp}
                disabled={mobileReorderProps?.isFirst}
                className="h-9 w-9"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={mobileReorderProps?.onMoveDown}
                disabled={mobileReorderProps?.isLast}
                className="h-9 w-9"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-9 w-9 sm:h-8 sm:w-8 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Video URL Input */}
        <div className="space-y-1.5">
          <Label htmlFor={`video-url-${block.id}`} className="text-xs text-muted-foreground">
            Video URL or Embed Code
          </Label>
          <Input
            id={`video-url-${block.id}`}
            value={block.url}
            onChange={(e) => handleUrlChange(e.target.value)}
            onBlur={handleBlur}
            placeholder="https://www.youtube.com/watch?v=... or paste iframe embed code"
            className={isTouched && validation.error ? "border-destructive" : ""}
          />
          
          {/* Validation Error */}
          {isTouched && validation.error && (
            <Alert variant="destructive" className="py-2">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-xs">
                {validation.error}
              </AlertDescription>
            </Alert>
          )}
          
          <p className="text-xs text-muted-foreground">
            Supports YouTube and Vimeo URLs or embed codes. The video will be embedded in the article.
          </p>
        </div>

        {/* Video Title */}
        <div className="space-y-1.5">
          <Label htmlFor={`video-title-${block.id}`} className="text-xs text-muted-foreground">
            Title (optional)
          </Label>
          <Input
            id={`video-title-${block.id}`}
            value={block.title || ""}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Add a title for the video..."
          />
        </div>

        {/* Video Preview */}
        {validation.isValid && validation.embedUrl && (
          <div className="mt-4 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground flex items-center gap-2">
                Preview
                {platformLabel && (
                  <span className="px-2 py-0.5 rounded-full bg-muted text-xs flex items-center gap-1">
                    {validation.platform === "youtube" && <Youtube className="h-3 w-3" />}
                    {platformLabel}
                  </span>
                )}
              </Label>
              {watchUrl && (
                <a
                  href={watchUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-primary hover:underline flex items-center gap-1"
                >
                  Watch on {platformLabel}
                  <ExternalLink className="h-3 w-3" />
                </a>
              )}
            </div>
            <div className="border rounded-lg overflow-hidden bg-muted/30">
              {block.title && (
                <p className="text-sm font-medium py-2 px-4 border-b bg-muted/50">
                  {block.title}
                </p>
              )}
              <div className="aspect-video">
                <iframe
                  src={validation.embedUrl}
                  title={block.title || "Embedded video"}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        )}

        {/* Empty state hint */}
        {!block.url && (
          <div className="border-2 border-dashed rounded-lg p-6 text-center bg-muted/20">
            <Video className="h-8 w-8 mx-auto text-muted-foreground/50 mb-2" />
            <p className="text-sm text-muted-foreground">
              Paste a YouTube or Vimeo URL above to embed a video
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
